// Belovedness B — the team-specific "untouchability" premium (research/trade-value-spec.md §3).
//
// ZenGM values a player symmetrically: a star is worth the same to every team, so a big
// enough package can always pry him loose (the "Tatum for Giddey + a pick" problem). B fixes
// this by adding a premium ONLY to the team that currently owns the player, ONLY on the
// outgoing side of its trade evaluation. The premium is quadratic in B, so franchise
// cornerstones become genuinely untouchable while role players are unaffected.
//
// This ports research/src/belovedness.js onto live ZenGM player data (draft.tid, stats
// tenure, awards, trade-value z-score). It is layered on top of ZenGM's existing value —
// durability/injury and contract are already handled by ValueChangeCalculator, so B does not
// re-derive them.

import type { Player, PlayerWithoutKey } from "../../../common/types.ts";

// Feature flag. Flip to false to fall back to stock ZenGM trade valuation for A/B testing.
export const CUSTOM_TRADE_LOGIC = true;

// Premium scale, in ValueChangeCalculator's post-exponent contribution units (a superstar
// contributes ~2^7 ≈ 128 to a team's trade value). k·B² for a max-B cornerstone is ~200,
// so his effective ask roughly triples — only a godfather haul clears it. Tunable.
const K = 250;

// Component weights (mirror research/src/config.js belovedness.weights).
const W = { homegrown: 0.28, tenure: 0.17, franchiseAcc: 0.25, stardom: 0.22, face: 0.08 };
const TENURE_TAU = 4;
const ACC_SCALE = 8;
const STARDOM_ZSCORE_FULL = 2; // trade-value z-score that maps stardom to 1

const awardScore = (type: string): number => {
	if (type.includes("Most Valuable Player")) return 4;
	if (type === "Finals MVP") return 2;
	if (type === "Defensive Player of the Year") return 2;
	if (type.includes("All-League")) return 1.5;
	if (type === "Won Championship") return 1;
	if (type === "All-Star") return 0.5;
	return 0;
};

// Regular-season seasons the player has spent on this team.
const seasonsOnTeam = (p: Player | PlayerWithoutKey, tid: number): Set<number> => {
	const seasons = new Set<number>();
	for (const s of p.stats) {
		if (!s.playoffs && s.tid === tid) {
			seasons.add(s.season);
		}
	}
	return seasons;
};

/**
 * Belovedness B in [0,1] of a player to the team `tid` that currently owns him.
 * @param zscoreValue The player's trade-value z-score (ValueChangeCalculator already computes this).
 * @param isFace Whether he is the highest-value player on the roster (the franchise face).
 */
export const computeBelovedness = (
	p: Player | PlayerWithoutKey,
	tid: number,
	zscoreValue: number,
	isFace: boolean,
): number => {
	const teamSeasons = seasonsOnTeam(p, tid);

	const homegrown = p.draft.tid === tid ? 1 : 0;
	const tenure = 1 - Math.exp(-teamSeasons.size / TENURE_TAU);

	// Accolades earned while on this team.
	let accHere = 0;
	for (const a of p.awards) {
		if (teamSeasons.has(a.season)) {
			accHere += awardScore(a.type);
		}
	}
	const franchiseAcc = Math.min(1, accHere / ACC_SCALE);

	const stardom = Math.max(0, Math.min(1, zscoreValue / STARDOM_ZSCORE_FULL));
	const face = isFace ? 1 : 0;

	const b =
		W.homegrown * homegrown +
		W.tenure * tenure +
		W.franchiseAcc * franchiseAcc +
		W.stardom * stardom +
		W.face * face;

	return Math.max(0, Math.min(1, b));
};

/** The hold premium a team adds to keep a beloved player, in trade-value contribution units. */
export const belovednessPremium = (b: number): number => K * b * b;
